// Drives the `<.autocomplete_input>` core component: a plain text input with a
// filtered dropdown of known values below it. Filtering happens here, in the
// browser, against the full option list handed over in `data-options` — the
// lists are small (speakers, playlists, locations) and a round trip per
// keystroke isn't worth it.
//
// With `data-separator` set the input holds several values at once (e.g.
// "Thich Nhat Hanh; Sister Chan Khong"). Only the segment the caret is in gets
// completed, so typing the separator opens a fresh dropdown for the next value.
export const AutocompleteInput = {
  mounted() {
    this.input = this.el.querySelector("[data-autocomplete-input]");
    this.list = this.el.querySelector("[data-autocomplete-list]");
    this.separator = this.el.dataset.separator || null;
    this.event = this.el.dataset.event || null;
    this.field = this.el.dataset.field || null;
    this.options = this.parseOptions();
    this.matches = [];
    this.activeIndex = -1;

    this.input.addEventListener("input", () => this.open());
    this.input.addEventListener("focus", () => this.open());
    this.input.addEventListener("keydown", (event) => this.onKeydown(event));
    this.input.addEventListener("blur", () => this.close());

    // mousedown rather than click, so the input never loses focus: a blur would
    // push the pre-selection value to the server and the patch coming back
    // would undo the selection we are about to make.
    this.list.addEventListener("mousedown", (event) => {
      const item = event.target.closest("[data-autocomplete-option]");
      if (!item) return;
      event.preventDefault();
      this.select(item.dataset.autocompleteOption);
    });
  },

  updated() {
    this.options = this.parseOptions();
  },

  parseOptions() {
    try {
      return JSON.parse(this.el.dataset.options || "[]");
    } catch (_error) {
      return [];
    }
  },

  segments() {
    return this.separator
      ? this.input.value.split(this.separator)
      : [this.input.value];
  },

  // What the user is typing right now — everything after the last separator.
  query() {
    const segments = this.segments();
    return segments[segments.length - 1].trim();
  },

  // The values already committed in the other segments, so we don't offer them twice.
  taken() {
    const segments = this.segments();
    return segments
      .slice(0, -1)
      .map((segment) => segment.trim().toLowerCase())
      .filter(Boolean);
  },

  open() {
    const query = this.query().toLowerCase();
    const taken = this.taken();

    this.matches = this.options
      .filter((option) => !taken.includes(option.toLowerCase()))
      .filter((option) => !query || option.toLowerCase().includes(query))
      .slice(0, 50);

    this.activeIndex = -1;
    this.render();
  },

  close() {
    this.matches = [];
    this.activeIndex = -1;
    this.render();
  },

  render() {
    if (!this.matches.length) {
      this.list.innerHTML = "";
      this.list.classList.add("hidden");
      return;
    }

    this.list.classList.remove("hidden");
    this.list.innerHTML = this.matches
      .map(
        (option, index) =>
          `<li data-autocomplete-option="${escapeAttribute(option)}" class="cursor-pointer px-3 py-1.5 text-sm ${
            index === this.activeIndex ? "bg-robe-100" : ""
          }">${escapeText(option)}</li>`,
      )
      .join("");
  },

  onKeydown(event) {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!this.matches.length) {
        this.open();
        if (!this.matches.length) return;
      }
      event.preventDefault();

      const step = event.key === "ArrowDown" ? 1 : -1;
      this.activeIndex =
        (this.activeIndex + step + this.matches.length) % this.matches.length;
      this.render();
      this.list
        .querySelector("[data-autocomplete-option].bg-robe-100")
        ?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter" && this.activeIndex >= 0) {
      event.preventDefault();
      this.select(this.matches[this.activeIndex]);
    } else if (event.key === "Escape") {
      this.close();
    }
  },

  select(value) {
    if (this.separator) {
      const segments = this.segments();
      // No space after the separator: the backend never trims these values, so "A; B"
      // would end up as a speaker/playlist literally named " B".
      segments[segments.length - 1] = value;
      this.input.value = segments.join(this.separator);
    } else {
      this.input.value = value;
    }

    this.close();
    this.input.focus();

    if (this.event) {
      this.pushEvent(this.event, {
        field: this.field,
        value: this.input.value,
      });
    }
  },
};

function escapeText(value) {
  const el = document.createElement("div");
  el.textContent = value;
  return el.innerHTML;
}

function escapeAttribute(value) {
  return escapeText(value).replace(/"/g, "&quot;");
}
